import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  TextField,
  Toolbar,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import SelectChannel from '../../channels/Select';
import { CompetitionInput } from '../../competitions/CompetitionInput';
import MatchInput from '../../matches/Input';
import TeamInput from '../../teams/Input';
import { IBroadcastAffiliation } from '../typings';

interface ICompetitionOption {
  value: string;
  label: string;
}

interface ITargetRow {
  key: number;
  competition: string | null;
  competitionOption: ICompetitionOption | null;
  team: string | null;
  match: string | null;
}

// Payload sent to the API, without the locale (added by the caller: project locale on
// creation, the affiliation locale on update).
export interface IBroadcastAffiliationPayload {
  channel: string | null;
  url: string;
  label: string | null;
  useLabel: boolean;
  isEnabled: boolean;
  startDate: string | null;
  endDate: string | null;
  targets: object[];
}

interface IProps extends Partial<InjectedIntlProps> {
  // Existing affiliation to edit; omit for the creation form.
  initial?: IBroadcastAffiliation;
  onSubmit: (payload: IBroadcastAffiliationPayload) => void;
  // Extra buttons rendered next to the submit one (e.g. delete on the edit form).
  extraActions?: React.ReactNode;
}

interface IState {
  channel: string | null;
  url: string;
  label: string;
  useLabel: boolean;
  enabled: boolean;
  startDate: string;
  endDate: string;
  targets: ITargetRow[];
  rowCounter: number;
}

const iri = (entity: any): string | null => (entity && entity['@id'] ? entity['@id'] : null);

// @ts-ignore
@injectIntl
export class BroadcastAffiliationForm extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const a = props.initial;
    const targets = a
      ? (a.targets || []).map((t, i) => ({
          key: i,
          competition: iri(t.competition),
          competitionOption: t.competition
            ? { value: iri(t.competition) as string, label: (t.competition as any).name }
            : null,
          team: iri(t.team),
          match: iri(t.match),
        }))
      : [{ key: 0, competition: null, competitionOption: null, team: null, match: null }];

    this.state = {
      channel: a ? iri(a.channel) : null,
      url: a ? a.url : '',
      label: (a && a.label) || '',
      useLabel: a ? a.useLabel : false,
      enabled: a ? a.isEnabled : true,
      startDate: a && a.startDate ? a.startDate.slice(0, 10) : '',
      endDate: a && a.endDate ? a.endDate.slice(0, 10) : '',
      targets,
      rowCounter: targets.length,
    };
  }

  private isFormValid = () => {
    const { channel, url } = this.state;
    return Boolean(channel && url);
  };

  public render() {
    const m = this.props.intl!.messages;

    return (
      <div style={{ padding: 16 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={this.state.enabled}
              onChange={(e) => this.setState({ enabled: e.target.checked })}
            />
          }
          label={m.field_enabled}
        />

        <div style={{ marginBottom: 16 }}>
          <SelectChannel
            input={{
              name: 'channel',
              value: this.state.channel,
              onChange: (value: string | null) => this.setState({ channel: value }),
            }}
          />
        </div>

        <TextField
          fullWidth
          label={m.field_url}
          value={this.state.url}
          onChange={(e) => this.setState({ url: e.target.value })}
          style={{ marginBottom: 16 }}
        />

        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={this.state.useLabel}
                onChange={(e) => this.setState({ useLabel: e.target.checked })}
              />
            }
            label={m.field_useLabel}
          />
        </div>

        <TextField
          fullWidth
          label={m.field_label}
          value={this.state.label}
          onChange={(e) => this.setState({ label: e.target.value })}
          disabled={!this.state.useLabel}
          style={{ marginBottom: 16 }}
        />

        <div style={{ display: 'flex', gap: 16, marginTop: 8, marginBottom: 8 }}>
          <TextField
            type="date"
            label={m.field_startDate}
            value={this.state.startDate}
            onChange={(e) => this.setState({ startDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label={m.field_endDate}
            value={this.state.endDate}
            onChange={(e) => this.setState({ endDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <Toolbar disableGutters>
          <Typography variant="subtitle1">{m.field_targets}</Typography>
          <div style={{ flex: 1 }} />
          <Button size="small" onClick={this.addTargetRow}>
            <AddIcon style={{ marginRight: 4 }} />
            {m.button_add}
          </Button>
        </Toolbar>

        {this.state.targets.map((target) => (
          <div
            key={target.key}
            style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 12 }}
          >
            <div style={{ flex: 1 }}>
              <CompetitionInput
                input={{
                  name: 'competition',
                  value: target.competition,
                  onChange: (value: string | null) => this.updateTarget(target.key, 'competition', value),
                }}
                selectedCompetitionOption={target.competitionOption}
              />
            </div>
            <div style={{ flex: 1 }}>
              <TeamInput
                input={{
                  name: 'team',
                  value: target.team,
                  onChange: (value: string | null) => this.updateTarget(target.key, 'team', value),
                }}
                competition={target.competition}
              />
            </div>
            <div style={{ flex: 1 }}>
              <MatchInput
                input={{
                  name: 'match',
                  value: target.match,
                  onChange: (value: string | null) => this.updateTarget(target.key, 'match', value),
                }}
                competition={target.competition}
              />
            </div>
            <IconButton onClick={() => this.removeTargetRow(target.key)} title={m.button_delete as string}>
              <DeleteIcon />
            </IconButton>
          </div>
        ))}

        <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleSubmit}
            disabled={!this.isFormValid()}
          >
            {m.button_validate}
          </Button>
          {this.props.extraActions && (
            <>
              <div style={{ flex: 1 }} />
              {this.props.extraActions}
            </>
          )}
        </div>
      </div>
    );
  }

  private addTargetRow = () => {
    this.setState((prev) => ({
      rowCounter: prev.rowCounter + 1,
      targets: [
        ...prev.targets,
        { key: prev.rowCounter, competition: null, competitionOption: null, team: null, match: null },
      ],
    }));
  };

  private removeTargetRow = (key: number) => {
    this.setState((prev) => ({
      targets: prev.targets.filter((t) => t.key !== key),
    }));
  };

  private updateTarget = (key: number, field: 'competition' | 'team' | 'match', value: string | null) => {
    this.setState((prev) => ({
      targets: prev.targets.map((t) => (t.key === key ? { ...t, [field]: value } : t)),
    }));
  };

  private handleSubmit = () => {
    const { channel, url, label, useLabel, enabled, startDate, endDate, targets } = this.state;

    if (!this.isFormValid()) return;

    this.props.onSubmit({
      channel,
      url,
      label: label || null,
      useLabel,
      isEnabled: enabled,
      startDate: startDate || null,
      endDate: endDate || null,
      targets: targets.map((t) => ({
        ...(t.competition ? { competition: t.competition } : {}),
        ...(t.team ? { team: t.team } : {}),
        ...(t.match ? { match: t.match } : {}),
      })),
    });
  };
}
